// @flow
import * as React from 'react';
import PropTypes from 'prop-types';
import TemplateTile from '../template-tile/TemplateTileComponent';
import * as abstractWindow from '../../../../abstraction/window';

import type { ProductTemplates } from '../../../../api/types/ProductTemplates';

import './TemplateSelectionTiles.scss';

type Props = {
  loadMore: Function,
  hasMore: boolean,
  isLoading: boolean,
  templates: ProductTemplates,
};

/**
 * Grid of Template tiles for the Creation Flow
 */
class TemplateSelectionTilesComponent extends React.Component<Props> {
  templateContainer: ?HTMLElement;

  raf: ?number = null;

  isLoading: boolean = false;

  height: number;

  static displayName = 'TemplateSelectionTiles';

  static propTypes = {
    loadMore: PropTypes.func.isRequired,
    hasMore: PropTypes.bool.isRequired,
    isLoading: PropTypes.bool.isRequired,
    templates: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        paperOption: PropTypes.string.isRequired,
        paperOptionRestricted: PropTypes.bool.isRequired,
        images: PropTypes.shape({
          thumb: PropTypes.string.isRequired,
        }).isRequired,
        endpoints: PropTypes.shape({
          pixelCreateUrl: PropTypes.string.isRequired,
        }).isRequired,
        quantities: PropTypes.shape({
          minimum: PropTypes.number,
          maximum: PropTypes.number,
        }),
      }),
    ).isRequired,
  };

  constructor(props: Props) {
    super(props);

    this.raf = null;
    this.isLoading = props.isLoading;
    this.templateContainer = null;
  }

  componentDidMount() {
    this.height = abstractWindow.getInnerHeight();
    this.raf = window.requestAnimationFrame(this.cycle);
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    // Clear isLoading when the templates are received
    if (this.isLoading && !nextProps.isLoading) {
      this.isLoading = false;
    }
    // Restart the cycle when hasMore changes to true
    if (!this.hasMore && nextProps.hasMore) {
      this.raf = window.requestAnimationFrame(this.cycle);
    }
  }

  componentWillUnmount() {
    window.cancelAnimationFrame(this.raf);
  }

  setTemplateContainer = (component: HTMLElement): void => {
    this.templateContainer = component;
  };

  cycle = (): void => {
    if (this.templateContainer === null) {
      return;
    }
    this.height = abstractWindow.getInnerHeight();
    const canLoadMore = this.props.hasMore && !this.isLoading;
    const isAtPageBottom =
      this.templateContainer && this.templateContainer.getBoundingClientRect().bottom - 100 <= this.height;
    if (canLoadMore && isAtPageBottom) {
      this.isLoading = true;
      this.props.loadMore();
    }
    // Only keep the cycle active when more templates can be loaded
    if (this.props.hasMore) {
      this.raf = window.requestAnimationFrame(this.cycle);
    }
  };

  templateGenerator() {
    return this.props.templates.map((item) => {
      const quantity = {
        from: item.quantities.minimum || 0,
        to: item.quantities.maximum || 0,
      };
      return (
        <TemplateTile
          key={`template-${item.id}`}
          name={`${item.name}`}
          image={item.images.thumb}
          paperOption={item.paperOption}
          paperOptionRestricted={item.paperOptionRestricted}
          quantity={quantity}
          creationLink={item.endpoints.pixelCreateUrl}
        />
      );
    });
  }

  render() {
    return (
      <div className="template-selection-tiles-component">
        <div className="template-selection-tiles-component__layout" ref={this.setTemplateContainer}>
          {this.templateGenerator()}
        </div>
      </div>
    );
  }
}

export default TemplateSelectionTilesComponent;
